/**
 * Application Logger
 *
 * Centralized logging service for the application.
 * Currently logs to console in all environments.
 * In the future, can be extended to send logs to external services (Sentry, LogRocket, etc.)
 *
 * Usage:
 *   import { logger } from '@/lib/logger';
 *   logger.error('Something went wrong', { userId: 123, action: 'upload' });
 *   logger.warn('Rate limit approaching');
 *   logger.info('User logged in');
 *   logger.debug('Cache hit', { key: 'photos-list' });
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

type LogContext = {
  [key: string]: unknown;
};

/**
 * Log an error message with optional context
 */
function error(message: string, context?: LogContext): void {
  // eslint-disable-next-line no-console
  console.error(`[ERROR] ${message}`, context || '');

  // TODO: In production, send to error tracking service (Sentry, etc.)
  // if (import.meta.env.PROD) {
  //   Sentry.captureException(new Error(message), { extra: context });
  // }
}

/**
 * Log a warning message with optional context
 */
function warn(message: string, context?: LogContext): void {
  // eslint-disable-next-line no-console
  console.warn(`[WARN] ${message}`, context || '');
}

/**
 * Log an info message with optional context
 */
function info(message: string, context?: LogContext): void {
  // eslint-disable-next-line no-console
  console.info(`[INFO] ${message}`, context || '');
}

/**
 * Log a debug message with optional context
 * Only logs in development mode
 */
function debug(message: string, context?: LogContext): void {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.debug(`[DEBUG] ${message}`, context || '');
  }
}

/**
 * Generic log function with configurable level
 */
function log(level: LogLevel, message: string, context?: LogContext): void {
  switch (level) {
    case 'error':
      error(message, context);
      break;
    case 'warn':
      warn(message, context);
      break;
    case 'info':
      info(message, context);
      break;
    case 'debug':
      debug(message, context);
      break;
  }
}

export const logger = {
  error,
  warn,
  info,
  debug,
  log,
};
